/**
 * Session Timeout Service
 * 
 * Security: Implements automatic session timeout to prevent unauthorized access
 * if a user's session is left idle for too long.
 */

import { doLogout } from "app/views/sessions/session.service";

// SECURITY: Inactivity timeout must match backend enforcement (20 minutes)
const SESSION_TIMEOUT_MS = 20 * 60 * 1000;
// UX: Warn user shortly before automatic logout
const WARNING_BEFORE_TIMEOUT_MS = 60 * 1000; // 1 minute
const SESSION_TIMEOUT_WARNING_EVENT = "nrb:session-timeout-warning";
let timeoutId = null;
let warningTimeoutId = null;
let lastActivityTime = Date.now();

/**
 * Initialize session timeout monitoring
 * Resets the timeout timer and sets up activity listeners
 */
export const initSessionTimeout = () => {  
  // Clear any existing timeout
  clearSessionTimeout();

  // Reset last activity time
  lastActivityTime = Date.now();

  // Set up activity listeners
  const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
  events.forEach(event => {
    document.addEventListener(event, resetSessionTimeout, true);
  });

  // Start the timeout
  startSessionTimeout();
};

/**
 * Reset the session timeout timer
 * Called on user activity
 */
export const resetSessionTimeout = () => {
  lastActivityTime = Date.now();
  startSessionTimeout();
};

/**
 * Notify UI layer to show warning popup.
 * SECURITY: Server still enforces timeout; this is only a UX warning.
 */
const dispatchTimeoutWarning = (remainingMs) => {
  try {
    window.dispatchEvent(
      new CustomEvent(SESSION_TIMEOUT_WARNING_EVENT, {
        detail: { remainingMs },
      })
    );
  } catch (e) {
    // Ignore UI notification issues
  }
};

/**
 * Start the session timeout countdown
 */
const startSessionTimeout = () => {
  // Clear existing timeout
  if (timeoutId) {
    clearTimeout(timeoutId);
  }
  if (warningTimeoutId) {
    clearTimeout(warningTimeoutId);
  }

  // Calculate time until timeout
  const timeSinceLastActivity = Date.now() - lastActivityTime;
  const timeUntilTimeout = SESSION_TIMEOUT_MS - timeSinceLastActivity;

  if (timeUntilTimeout > 0) {
    // Schedule warning popup shortly before logout
    const timeUntilWarning = timeUntilTimeout - WARNING_BEFORE_TIMEOUT_MS;
    if (timeUntilWarning > 0) {
      warningTimeoutId = setTimeout(() => {
        dispatchTimeoutWarning(WARNING_BEFORE_TIMEOUT_MS);
      }, timeUntilWarning);
    } else {
      // If we're already inside the warning window, warn immediately
      dispatchTimeoutWarning(Math.max(0, timeUntilTimeout));
    }

    timeoutId = setTimeout(() => {
      handleSessionTimeout();
    }, timeUntilTimeout);
  } else {
    // Already timed out
    handleSessionTimeout();
  }
};

/**
 * Handle session timeout
 * SECURITY: Calls backend logout to invalidate token, then clears session and redirects to login
 */
const handleSessionTimeout = async () => {
  // SECURITY: Call backend logout to invalidate token server-side
  await doLogout();

  // Redirect to login page
  window.location.href = '/session/signin/step1';
};

/**
 * Clear the session timeout
 * Should be called on logout or when component unmounts
 */
export const clearSessionTimeout = () => {
  if (timeoutId) {
    clearTimeout(timeoutId);
    timeoutId = null;
  }
  if (warningTimeoutId) {
    clearTimeout(warningTimeoutId);
    warningTimeoutId = null;
  }

  // Remove event listeners
  const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
  events.forEach(event => {
    document.removeEventListener(event, resetSessionTimeout, true);
  });
};

// Export event name for UI listeners
export const SESSION_TIMEOUT_WARNING_EVENT_NAME = SESSION_TIMEOUT_WARNING_EVENT;

/**
 * Get remaining session time in milliseconds
 */
export const getRemainingSessionTime = () => {
  const timeSinceLastActivity = Date.now() - lastActivityTime;
  return Math.max(0, SESSION_TIMEOUT_MS - timeSinceLastActivity);
};
